import type { MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { DeleteOutlined, PaperClipOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, Modal, Spin } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';

import { useUpdateReviewMutation } from '@/api/endpoints/mainPages';
import { DataSection, ImageUploadField, InputField } from '@/shared/components';
import { useApiRequest } from '@/shared/hooks';

import { REVIEW_VALIDATION_SCHEMA } from '../../consts';
import type { IReviewItemData, IFormData } from '../../types';

import styles from './ReviewModals.module.scss';

interface IProp {
  pageId: number;
  isModalOpen: boolean;
  handleCancel: VoidFunction;
  handleClearOperateItem: VoidFunction;
  itemToOperate: IReviewItemData;
}

export const EditReviewModal = ({ pageId, isModalOpen, handleCancel, itemToOperate }: IProp) => {
  const [updateReview, { isLoading: isReviewUpdating }] = useUpdateReviewMutation();

  const sendApiRequest = useApiRequest();

  const methods = useForm<IFormData>({
    defaultValues: {
      name: itemToOperate ? itemToOperate.name : '',
      description: itemToOperate ? itemToOperate.description : '',
      photo: itemToOperate ? itemToOperate.photo : null,
    },
    resolver: yupResolver(REVIEW_VALIDATION_SCHEMA),
  });

  const [isImageChanged, setIsImageChanged] = useState(false);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const { handleSubmit, watch, setValue } = methods;

  const handleUpdateReview = handleSubmit(({ name, description, photo }) => {
    if (photo) {
      sendApiRequest({
        successMessage: 'Отзыв успешно обновлён',
        apiRequestCallback: () =>
          updateReview({
            mainPageId: pageId,
            reviewId: itemToOperate.id,
            data: {
              name,
              description,
              ...(photo?.signed_id && { photo: photo.signed_id }),
            },
          }),
        successCallback: handleCancel,
      });
    }
  });

  const photo = watch('photo');

  const getImageName = () => {
    if (!photo) {
      return '';
    }

    return photo.filename;
  };

  const removeUploadedImage = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setValue('photo', null);
    setImageUrl('');
    setIsImageChanged(false);
  };

  const imageRequestCallback = (file: File) => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
  };

  const imageChangeCallback = useCallback(() => setIsImageChanged(true), []);

  useEffect(() => {
    if (!photo?.url) {
      return;
    }

    if (!isImageChanged) {
      setImageUrl(photo.url);
    }
  }, [isImageChanged, photo]);

  return (
    <Modal
      width={600}
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <Spin spinning={isReviewUpdating}>
        <div className={styles.wrapper}>
          <FormProvider {...methods}>
            <p className={styles.title}>Изменение отзыва</p>
            <div className={styles['content-wrapper']}>
              <DataSection
                titleText="Имя и фамилия студента"
                isTitleRequired
              >
                <InputField
                  fieldName="name"
                  placeholder="Имя и фамилия"
                  shouldEnableLengthCounter
                  maxLengthCount={200}
                />
              </DataSection>
              <DataSection
                titleText="Текст отзыва"
                isTitleRequired
              >
                <InputField
                  fieldName="description"
                  placeholder="Текст"
                  shouldEnableLengthCounter
                  maxLengthCount={500}
                />
              </DataSection>
              <DataSection
                titleText="Фото студента"
                isTitleRequired
              >
                <ImageUploadField
                  accept="image/png,image/jpeg,image/jpg"
                  name="Фото студента"
                  fieldName="photo"
                  className={photo ? styles['image-uploaded'] : ''}
                  requestCallback={imageRequestCallback}
                  changeCallback={imageChangeCallback}
                  showUploadList={false}
                />
                {photo && (
                  <>
                    <Button
                      size="small"
                      onClick={() => setIsImagePreviewOpen(true)}
                      type="text"
                      icon={<PaperClipOutlined />}
                      className={styles['image-button-wrapper']}
                    >
                      {getImageName()}
                      <DeleteOutlined
                        className={styles['remove-image-icon']}
                        onClick={removeUploadedImage}
                      />
                    </Button>
                    <Image
                      alt="Фото студента"
                      style={{ display: 'none' }}
                      preview={{
                        open: isImagePreviewOpen,
                        src: imageUrl,
                        onOpenChange: (value) => setIsImagePreviewOpen(value),
                      }}
                    />
                  </>
                )}
              </DataSection>
            </div>
            <div className={styles['buttons-wrapper']}>
              <Button
                size="large"
                onClick={handleCancel}
                className={styles.button}
                danger
              >
                Отмена
              </Button>
              <Button
                size="large"
                onClick={handleUpdateReview}
                disabled={isReviewUpdating}
                type="primary"
                className={styles.button}
              >
                Сохранить
              </Button>
            </div>
          </FormProvider>
        </div>
      </Spin>
    </Modal>
  );
};
